import React, { useState, useEffect } from 'react';
import theme from '../../../theme/globalTheme';
import {
  Table,
  Typography,
  Select,
  Layout,
  Card,
  Space,
  Tag,
  Tooltip,
  Button,
  message,
  Spin,
  Alert,
  Badge,
  DatePicker,
  Popconfirm,
  Tabs,
  Calendar,
  Statistic,
  Row,
  Col,
  Modal
} from 'antd';
import {
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  SaveOutlined,
  SyncOutlined,
  LeftOutlined,
  RightOutlined,
  TeamOutlined,
  TableOutlined,
  BarChartOutlined,
  EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { supabase } from '../../../api/supabase';

dayjs.locale('fr');

const { Title, Text } = Typography;
const { Option } = Select;
const { Header, Content } = Layout;
const { useToken } = theme;
const { TabPane } = Tabs;

const ROLES = {
    base: { color: 'blue', text: 'Base' },
    navigation: { color: theme.colors.white, text: 'Navigation' },
    astreinte: { color: 'orange', text: 'Astreinte' },
    mecano: { color: 'cyan', text: 'Mécano' },
    jetski: { color: 'geekblue', text: 'Jetski' },
    seabob: { color: 'purple', text: 'Seabob' },
    off: { color: theme.colors.textTertiary, text: 'OFF' },
    'off am': { color: theme.colors.textTertiary, text: 'OFF Matin' },
    'off pm': { color: theme.colors.textTertiary, text: 'OFF Après-midi' },
    vacances: { color: 'gold', text: 'Vacances' },
    malade: { color: 'magenta', text: 'Malade' }
};

const EmployeeSchedule = () => {
  // États existants pour la vue tableau
  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [hasChanges, setHasChanges] = useState(false);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);

  // Nouveaux états pour la vue calendrier
  const [activeTab, setActiveTab] = useState('table');
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [monthlySchedules, setMonthlySchedules] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState({});
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [dailySchedules, setDailySchedules] = useState([]);

  const { token } = useToken();
  const [messageApi, contextHolder] = message.useMessage();

  // Fonction existante pour charger les données quotidiennes
  const loadData = async (date = selectedDate) => {
    try {
      setLoading(true);
      setError(null);

      const targetDate = date.format('YYYY-MM-DD');
      const nextDay = date.add(1, 'day').format('YYYY-MM-DD');
      const lastTwoMonths = date.subtract(60, 'days').format('YYYY-MM-DD');

      // Load active employees
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (empError) throw empError;

      // Load schedules for target dates and last two months
      const { data: schedules, error: schedError } = await supabase
        .from('employee_schedules')
        .select('*')
        .gte('schedule_date', lastTwoMonths)
        .lte('schedule_date', nextDay);

      if (schedError) throw schedError;

      // Process employee data with improved off day calculation
      const processedData = employees.map(employee => {
        const employeeSchedules = schedules
          .filter(s => s.employee_id === employee.id)
          .sort((a, b) => dayjs(b.schedule_date).diff(dayjs(a.schedule_date)));
        
        let lastOffDay = 0;
        let consecutiveWorkDays = 0;
        let lastOffDate = null;

        for (let i = 0; i <= 60; i++) {
          const checkDate = dayjs(targetDate).subtract(i, 'days').format('YYYY-MM-DD');
          const daySchedule = employeeSchedules.find(s => s.schedule_date === checkDate);
          const role = (daySchedule?.schedule_type || '').toLowerCase();

          if (role.includes('off') || role === 'vacances' || role === 'malade' || role === '') {
            lastOffDay = i;
            lastOffDate = checkDate;
            break;
          } else if (daySchedule) {
            consecutiveWorkDays++;
          }
        }

        const daySchedule = employeeSchedules.find(s => s.schedule_date === targetDate);
        const nextDaySchedule = employeeSchedules.find(s => s.schedule_date === nextDay);

        return {
          id: employee.id,
          name: employee.name,
          role: employee.role,
          lastOffDay,
          lastOffDate: lastOffDate ? dayjs(lastOffDate).format('DD/MM') : 'N/A',
          consecutiveWorkDays,
          todayRole: daySchedule?.schedule_type?.toUpperCase() || '',
          tomorrowRole: nextDaySchedule?.schedule_type?.toUpperCase() || '',
          needsAttention: consecutiveWorkDays >= 6
        };
      });

      setStaffData(processedData);
      setEmployees(employees);
      setHasChanges(false);
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message);
      messageApi.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Nouvelle fonction pour charger les données mensuelles
  const loadMonthData = async (month = currentMonth) => {
    try {
      setLoading(true);
      
      const startOfMonth = month.startOf('month').format('YYYY-MM-DD');
      const endOfMonth = month.endOf('month').format('YYYY-MM-DD');

      // Charger les employés actifs
      const { data: employeesData, error: empError } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (empError) throw empError;

      // Charger les plannings du mois
      const { data: schedulesData, error: schedError } = await supabase
        .from('employee_schedules')
        .select('*')
        .gte('schedule_date', startOfMonth)
        .lte('schedule_date', endOfMonth);

      if (schedError) throw schedError;

      setEmployees(employeesData);
      setMonthlySchedules(schedulesData);
      
      // Calculer les statistiques mensuelles
      calculateMonthlyStats(employeesData, schedulesData, month);
      
    } catch (err) {
      console.error('Erreur de chargement:', err);
      messageApi.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Calculer les statistiques mensuelles
  const calculateMonthlyStats = (employeesData, schedulesData, month) => {
    const stats = {};
    const totalDaysInMonth = month.daysInMonth();

    employeesData.forEach(employee => {
      const employeeSchedules = schedulesData.filter(s => s.employee_id === employee.id);
      
      const offDays = employeeSchedules.filter(s => 
        s.schedule_type?.toLowerCase().includes('off') || 
        s.schedule_type?.toLowerCase() === 'vacances' ||
        s.schedule_type?.toLowerCase() === 'malade'
      ).length;

      const workDays = employeeSchedules.filter(s => 
        s.schedule_type && 
        !s.schedule_type.toLowerCase().includes('off') &&
        s.schedule_type.toLowerCase() !== 'vacances' &&
        s.schedule_type.toLowerCase() !== 'malade'
      ).length;

      const unscheduledDays = totalDaysInMonth - employeeSchedules.length;

      stats[employee.id] = {
        name: employee.name,
        role: employee.role,
        offDays,
        workDays,
        unscheduledDays,
        totalScheduled: employeeSchedules.length,
        schedulesByType: employeeSchedules.reduce((acc, s) => {
          const type = s.schedule_type?.toLowerCase() || 'non-assigné';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {})
      };
    });

    setMonthlyStats(stats);
  };

  useEffect(() => {
    if (activeTab === 'table') {
      loadData(selectedDate);
    } else if (activeTab === 'calendar') {
      loadMonthData(currentMonth);
    }
  }, [selectedDate, currentMonth, activeTab]);

  // Fonctions existantes (updateSchedule, saveSchedules, assignAllToNavigation, renderRoleSelect)
  const updateSchedule = async (employeeId, date, scheduleType) => {
    try {
      const { error } = await supabase
        .from('employee_schedules')
        .upsert({
          employee_id: employeeId,
          schedule_date: date,
          schedule_type: scheduleType
        }, {
          onConflict: 'employee_id,schedule_date'
        });

      if (error) throw error;

      messageApi.success('Planning mis à jour');
      if (activeTab === 'table') {
        await loadData(selectedDate);
      } else {
        await loadMonthData(currentMonth);
      }
    } catch (err) {
      console.error('Update failed:', err);
      messageApi.error(`Échec de la mise à jour: ${err.message}`);
    }
  };

  const saveSchedules = async () => {
    try {
      setSaving(true);
      if (activeTab === 'table') {
        await loadData(selectedDate);
      } else {
        await loadMonthData(currentMonth);
      }
      setHasChanges(false);
      messageApi.success('Schedule saved successfully');
    } catch (err) {
      messageApi.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const assignAllToNavigation = async (forDay) => {
    try {
      setBulkAssignLoading(true);
      const date = forDay === 'today' 
        ? selectedDate.format('YYYY-MM-DD') 
        : selectedDate.add(1, 'day').format('YYYY-MM-DD');
      
      const updates = staffData.map(employee => ({
        employee_id: employee.id,
        schedule_date: date,
        schedule_type: 'navigation'
      }));
      
      const { error } = await supabase
        .from('employee_schedules')
        .upsert(updates, {
          onConflict: 'employee_id,schedule_date'
        });
        
      if (error) throw error;
      
      messageApi.success(`Tout le personnel assigné à la navigation pour le ${dayjs(date).format('DD/MM')}`);
      await loadData(selectedDate);
    } catch (err) {
      console.error('Bulk assign error:', err);
      messageApi.error(`Échec de l'assignation groupée: ${err.message}`);
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const renderRoleSelect = (staffMember, isToday) => {
    const date = isToday ? 
      selectedDate.format('YYYY-MM-DD') : 
      selectedDate.add(1, 'day').format('YYYY-MM-DD');
    const currentValue = isToday ? staffMember.todayRole : staffMember.tomorrowRole;
  
    return (
      <Select
        value={currentValue || undefined}
        onChange={(value) => updateSchedule(staffMember.id, date, value)}
        style={{ width: '100%' }}
        status={!currentValue ? 'error' : ''}
        placeholder="Sélectionner"
        bordered
      >
        {Object.entries(ROLES).map(([value, { color, text }]) => (
          <Option key={value} value={value}>
            <Tag color={color}>{text}</Tag>
          </Option>
        ))}
      </Select>
    );
  };

  // Nouvelles fonctions pour la vue calendrier
  const getDateSchedules = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    return monthlySchedules.filter(s => s.schedule_date === dateStr);
  };

  const dateCellRender = (date) => {
    const daySchedules = getDateSchedules(date);
    
    if (selectedEmployee !== 'all') {
      const employeeSchedule = daySchedules.find(s => s.employee_id === parseInt(selectedEmployee));
      if (employeeSchedule) {
        const role = ROLES[employeeSchedule.schedule_type?.toLowerCase()] || { color: 'default', text: employeeSchedule.schedule_type };
        return (
          <Badge 
            color={role.color} 
            text={role.text} 
            style={{ fontSize: '10px' }}
          />
        );
      }
      return null;
    }

    const scheduledCount = daySchedules.length;
    const totalEmployees = employees.length;
    
    if (scheduledCount === 0) return null;
    
    const color = scheduledCount === totalEmployees ? 'green' : 
                  scheduledCount > totalEmployees * 0.7 ? 'blue' : 'orange';
    
    return (
      <Badge 
        count={`${scheduledCount}/${totalEmployees}`} 
        style={{ 
          backgroundColor: color,
          fontSize: '10px'
        }} 
      />
    );
  };

  const onCalendarDateSelect = (date) => {
    setSelectedCalendarDate(date);
    const daySchedules = getDateSchedules(date);
    
    const completeDaySchedules = employees.map(employee => {
      const schedule = daySchedules.find(s => s.employee_id === employee.id);
      return {
        key: employee.id,
        employeeName: employee.name,
        employeeRole: employee.role,
        scheduleType: schedule?.schedule_type || 'Non assigné',
        hasSchedule: !!schedule
      };
    });
    
    setDailySchedules(completeDaySchedules);
    setDetailModalVisible(true);
  };

  // Colonnes pour les tableaux
  const columns = [
    {
      title: (
        <Space>
          <UserOutlined />
          <Text>Staff</Text>
        </Space>
      ),
      dataIndex: 'name',
      fixed: 'left',
      width: 150,
      render: (name, record) => (
        <Space>
          <Text strong>{name}</Text>
          <Tag color={record.role === 'Capitaine' ? 'gold' : 'blue'}>
            {record.role}
          </Tag>
          {record.needsAttention && (
            <Tooltip title={`${record.consecutiveWorkDays} consecutive working days`}>
              <WarningOutlined style={{ color: token.colorError }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: (
        <Space>
          <ClockCircleOutlined />
          <Text>Last Off</Text>
        </Space>
      ),
      dataIndex: 'lastOffDay',
      width: 100,
      render: (days, record) => (
        <Space>
          <Badge 
            count={days} 
            style={{ 
              backgroundColor: record.needsAttention ? token.colorError : token.colorSuccess 
            }} 
          />
          <Text type="secondary">{record.lastOffDate}</Text>
        </Space>
      ),
    },
    {
      title: (
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{selectedDate.format('DD/MM')}</span>
          <Popconfirm
            title="Assigner tout le monde à 'Navigation'?"
            description="Cette action remplacera les rôles existants pour la journée d'aujourd'hui."
            onConfirm={() => assignAllToNavigation('today')}
            okText="Oui"
            cancelText="Non"
          >
            <Button 
              type="primary" 
              size="small" 
              icon={<TeamOutlined />}
              loading={bulkAssignLoading}
              style={{ marginLeft: 8 }}
            >
              Tous Navigation
            </Button>
          </Popconfirm>
        </Space>
      ),
      dataIndex: 'todayRole',
      width: 230,
      render: (_, record) => renderRoleSelect(record, true),
    },
    {
      title: (
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{selectedDate.add(1, 'day').format('DD/MM')}</span>
          <Popconfirm
            title="Assigner tout le monde à 'Navigation'?"
            description="Cette action remplacera les rôles existants pour la journée de demain."
            onConfirm={() => assignAllToNavigation('tomorrow')}
            okText="Oui"
            cancelText="Non"
          >
            <Button 
              type="primary" 
              size="small" 
              icon={<TeamOutlined />}
              loading={bulkAssignLoading}
              style={{ marginLeft: 8 }}
            >
              Tous Navigation
            </Button>
          </Popconfirm>
        </Space>
      ),
      dataIndex: 'tomorrowRole',
      width: 230,
      render: (_, record) => renderRoleSelect(record, false),
    }
  ];

  const dailyColumns = [
    {
      title: 'Employé',
      dataIndex: 'employeeName',
      render: (name, record) => (
        <Space>
          <Text strong>{name}</Text>
          <Tag color={record.employeeRole === 'Capitaine' ? 'gold' : 'blue'}>
            {record.employeeRole}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Planning',
      dataIndex: 'scheduleType',
      render: (type, record) => {
        if (!record.hasSchedule) {
          return <Tag color="default">Non assigné</Tag>;
        }
        const role = ROLES[type?.toLowerCase()] || { color: 'default', text: type };
        return <Tag color={role.color}>{role.text}</Tag>;
      },
    }
  ];

  const statsColumns = [
    {
      title: 'Employé',
      dataIndex: 'name',
      width: 120,
      render: (name, record) => (
        <div>
          <Text strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
            {name}
          </Text>
          <Tag color={record.role === 'Capitaine' ? 'gold' : 'blue'} size="small">
            {record.role}
          </Tag>
        </div>
      ),
    },
    {
      title: 'OFF',
      dataIndex: 'offDays',
      width: 60,
      align: 'center',
      render: (days) => (
        <Badge count={days} style={{ backgroundColor: '#f50' }} />
      ),
    },
    {
      title: 'Travail',
      dataIndex: 'workDays',
      width: 70,
      align: 'center',
      render: (days) => (
        <Badge count={days} style={{ backgroundColor: theme.colors.lightGrey }} />
      ),
    },
    {
      title: 'Non prog.',
      dataIndex: 'unscheduledDays',
      width: 80,
      align: 'center',
      render: (days) => (
        <Badge count={days} style={{ backgroundColor: '#faad14' }} />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'totalScheduled',
      width: 70,
      align: 'center',
      render: (total) => (
        <Text>{total}/{currentMonth.daysInMonth()}</Text>
      ),
    }
  ];

  return (
    <Layout>
      {contextHolder}
      <Header 
        style={{ 
          position: 'fixed', 
          zIndex: 1, 
          width: '100%', 
          background: token.colorBgContainer,
          boxShadow: token.boxShadow,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Space>
          <CalendarOutlined />
          <Title level={3} style={{ margin: 0 }}>
            {activeTab === 'table' ? 'Staff Schedule' : 'Planning Mensuel'}
          </Title>
        </Space>
        <Space>
          {activeTab === 'table' ? (
            <>
              <Button
                icon={<LeftOutlined />}
                onClick={() => setSelectedDate(selectedDate.subtract(1, 'day'))}
              />
              <DatePicker
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                format="DD/MM/YYYY"
                allowClear={false}
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => setSelectedDate(selectedDate.add(1, 'day'))}
              />
            </>
          ) : (
            <>
              <Button
                icon={<LeftOutlined />}
                onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
              />
              <Text strong style={{ minWidth: '150px', textAlign: 'center' }}>
                {currentMonth.format('MMMM YYYY')}
              </Text>
              <Button
                icon={<RightOutlined />}
                onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
              />
              <Select
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                style={{ width: 200 }}
                placeholder="Sélectionner un employé"
              >
                <Option value="all">
                  <Space>
                    <TeamOutlined />
                    Vue d'ensemble
                  </Space>
                </Option>
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id.toString()}>
                    <Space>
                      <UserOutlined />
                      {emp.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            </>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={saveSchedules}
            loading={saving}
            disabled={!hasChanges}
          >
            Save Changes
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={() => activeTab === 'table' ? loadData(selectedDate) : loadMonthData(currentMonth)}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </Header>

      <Content style={{ marginTop: 64, padding: '16px 16px 0' }}>
        <Card bordered={false}>
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={[
              {
                key: 'table',
                label: (
                  <Space>
                    <TableOutlined />
                    Vue Tableau
                  </Space>
                ),
                children: (
                  <Spin spinning={loading}>
                    <Table
                      dataSource={staffData}
                      columns={columns}
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      size="small"
                      rowKey="id"
                      rowClassName={(record) => 
                        record.needsAttention ? 'ant-table-row-warning' : ''
                      }
                    />
                  </Spin>
                )
              },
              {
                key: 'calendar',
                label: (
                  <Space>
                    <CalendarOutlined />
                    Vue Calendrier
                  </Space>
                ),
                children: (
                  <Spin spinning={loading}>
                    <Row gutter={[16, 16]}>
                      {/* Statistiques mensuelles */}
                      <Col span={24}>
                        <Card 
                          title={
                            <Space>
                              <BarChartOutlined />
                              <Text>Statistiques - {currentMonth.format('MMMM YYYY')}</Text>
                            </Space>
                          }
                          size="small"
                        >
                          <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                              <Statistic
                                title="Total employés"
                                value={employees.length}
                                prefix={<UserOutlined />}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Statistic
                                title="Jours dans le mois"
                                value={currentMonth.daysInMonth()}
                                prefix={<CalendarOutlined />}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Statistic
                                title="Total plannings"
                                value={monthlySchedules.length}
                                valueStyle={{ color: '#3f8600' }}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={6}>
                              <Statistic
                                title="Taux de planification"
                                value={((monthlySchedules.length / (employees.length * currentMonth.daysInMonth())) * 100).toFixed(1)}
                                suffix="%"
                                valueStyle={{ color: '#cf1322' }}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Col>

                      {/* Calendrier */}
                      <Col xs={24} lg={16}>
                        <Card 
                          title={
                            <Space>
                              <CalendarOutlined />
                              <Text>Calendrier</Text>
                              {selectedEmployee !== 'all' && (
                                <Tag color="blue">
                                  {employees.find(e => e.id.toString() === selectedEmployee)?.name}
                                </Tag>
                              )}
                            </Space>
                          }
                          size="small"
                        >
                          <Calendar
                            value={currentMonth}
                            mode="month"
                            onPanelChange={(date) => setCurrentMonth(date)}
                            dateCellRender={dateCellRender}
                            onSelect={onCalendarDateSelect}
                          />
                        </Card>
                      </Col>

                      {/* Statistiques détaillées par employé */}
                      <Col xs={24} lg={8}>
                        <Card 
                          title={
                            <Space>
                              <UserOutlined />
                              <Text>Détail par employé</Text>
                            </Space>
                          }
                          size="small"
                          style={{ height: '500px' }}
                        >
                          <div style={{ height: '430px', overflow: 'auto' }}>
                            <Table
                              dataSource={Object.entries(monthlyStats).map(([id, stats]) => ({
                                key: id,
                                ...stats
                              }))}
                              columns={statsColumns}
                              pagination={false}
                              size="small"
                              scroll={{ x: 'max-content' }}
                              style={{ minWidth: '100%' }}
                            />
                          </div>
                        </Card>
                      </Col>
                    </Row>
                  </Spin>
                )
              }
            ]}
          />
        </Card>

        {/* Modal de détail quotidien */}
        <Modal
          title={
            <Space>
              <EyeOutlined />
              <Text>Détail du {selectedCalendarDate?.format('DD/MM/YYYY')}</Text>
            </Space>
          }
          open={detailModalVisible}
          onCancel={() => setDetailModalVisible(false)}
          footer={null}
          width={600}
        >
          <Table
            dataSource={dailySchedules}
            columns={dailyColumns}
            pagination={false}
            size="small"
            rowClassName={(record) => 
              !record.hasSchedule ? 'ant-table-row-warning' : ''
            }
          />
        </Modal>
      </Content>

      <style jsx global>{`
        .ant-table-row-warning {
          background-color: #fff7e6;
        }
        .ant-picker-calendar .ant-picker-calendar-date-content {
          height: 60px;
          overflow: hidden;
        }
        .ant-badge {
          font-size: 10px;
        }
      `}</style>
    </Layout>
  );
};

export default EmployeeSchedule;